import { useState } from 'react';
import { useAuth } from '@/app/AuthContext';
import { useSelection } from '@/app/SelectionContext';
import { useCategories, useMapCategory, useUnmappedRows } from '@/app/hooks';
import { Badge } from '@/components/Badge';
import { Spinner } from '@/components/LoadingScreen';
import { CategoryMappingRow } from './CategoryMappingRow';
import { ApiRequestError } from '@/services/api';
import { Permission } from '@/types/permission';
import { v } from '@/theme/tokens';

interface CategoryMappingStepProps {
  importId: string;
}

/**
 * Original Excel label → FinancialCategory. Rows come from the backend's
 * unmapped list; a saved mapping persists as a CategoryMapping alias so the
 * next month's report recognises the label automatically.
 */
export function CategoryMappingStep({ importId }: CategoryMappingStepProps) {
  const { can } = useAuth();
  const { units } = useSelection();
  const unmapped = useUnmappedRows(importId);
  const categories = useCategories();
  const mapCategory = useMapCategory();

  const [savingLabel, setSavingLabel] = useState<string | null>(null);
  const [mappedLabels, setMappedLabels] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const canMap = can(Permission.MANAGE_MAPPING);
  const rows = unmapped.data ?? [];
  const pending = rows.filter((row) => !mappedLabels.includes(row.raw_label));

  const save = async (rawLabel: string, categoryId: string) => {
    setSavingLabel(rawLabel);
    setError(null);
    try {
      await mapCategory.mutateAsync({ id: importId, rawLabel, categoryId });
      // The refetch removes the row; keep it visible as "mapped" for a beat.
      setMappedLabels((labels) => [...labels, rawLabel]);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Не удалось сохранить сопоставление');
    } finally {
      setSavingLabel(null);
    }
  };

  if (unmapped.isPending || categories.isPending) return <Spinner label="Загрузка статей…" />;
  if (unmapped.error || categories.error) {
    return (
      <div style={{ fontSize: 13, color: v('negative') }}>
        Не удалось загрузить статьи для сопоставления.
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: v('positive'), fontWeight: 600 }}>
        ✓ Все статьи распознаны
      </div>
    );
  }

  return (
    <div>
      {pending.length > 0 && (
        <div
          role="status"
          style={{
            background: v('warning-soft'),
            color: v('warning'),
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 10,
          }}
        >
          Необходимо сопоставить {pending.length}{' '}
          {pending.length === 1 ? 'статью' : pending.length < 5 ? 'статьи' : 'статей'} — без этого
          импорт не подтверждается.
        </div>
      )}

      {!canMap && (
        <div style={{ fontSize: 13, color: v('ink-muted'), marginBottom: 10 }}>
          Сопоставление выполняет владелец или бухгалтер.{' '}
          <Badge tone="neutral">только просмотр</Badge>
        </div>
      )}

      {error && (
        <div role="alert" style={{ color: v('negative'), fontSize: 13, marginBottom: 10 }}>{error}</div>
      )}

      {rows.map((row) => (
        <CategoryMappingRow
          key={row.id}
          row={row}
          unitName={units.find((u) => u.id === row.business_unit_id)?.name}
          categories={categories.data ?? []}
          canMap={canMap}
          saving={savingLabel === row.raw_label}
          justMapped={mappedLabels.includes(row.raw_label)}
          onSave={(rawLabel, categoryId) => void save(rawLabel, categoryId)}
        />
      ))}
    </div>
  );
}
